import type { TrackingStatus } from '../../../hooks/useLocationTracking';
import type { IObserverPosting, ObserverDataStatus } from '../../../interface/observer';

/** Within this many metres of the unit an observer counts as there: a phone fix is ±10–50 m outdoors. */
export const AT_UNIT_METRES = 150;

/**
 * A fix less precise than this (Wi-Fi or cell-tower positioning, desktops) cannot place someone
 * relative to one building: the distance is treated as unknown rather than acted on.
 */
export const COARSE_FIX_METRES = 1000;

/**
 * Distance to the unit as far as the fix can honestly tell: null when unknown or too coarse, and 0
 * ("at the unit") whenever the unit lies inside the fix's own error circle.
 */
export const trustedDistance = (distance: number | null, accuracy: number | null | undefined): number | null => {
	if (distance === null) return null;
	const error = accuracy ?? 0;
	if (error > COARSE_FIX_METRES) return null;
	return distance <= Math.max(AT_UNIT_METRES, error) ? 0 : distance;
};

export type ObserverNextStep =
	| { kind: 'location-off'; blocked: boolean }
	| { kind: 'awaiting-posting' }
	| { kind: 'head-to-unit'; place: string; latitude: number; longitude: number }
	| { kind: 'checklist' };

/**
 * The single thing the home screen asks an observer to do now, in order of what unblocks the most.
 *
 * <p>Location comes first because the desk relies on it for safety and every later step is easier
 * to trust with it. A posting that has not loaded yields nothing here: the pass already says so and
 * offers the retry, and inventing a step on unknown data would send someone the wrong way.</p>
 *
 * <p>"Head to your unit" only shows when the distance is KNOWN to be large — pass it through
 * {@link trustedDistance} first. Unknown distance (no PU coordinates, no fix, a coarse fix) falls
 * through to the checklist rather than blocking it.</p>
 */
export const deriveNextStep = ({
	dataStatus,
	posting,
	trackingStatus,
	distance,
}: {
	dataStatus: ObserverDataStatus;
	posting: IObserverPosting | undefined;
	trackingStatus: TrackingStatus | undefined;
	distance: number | null;
}): ObserverNextStep | null => {
	if (trackingStatus === 'permission-denied' || trackingStatus === 'needs-permission') {
		return { kind: 'location-off', blocked: trackingStatus === 'permission-denied' };
	}
	if (dataStatus !== 'ready') return null;
	if (!posting) return { kind: 'awaiting-posting' };
	if (
		distance !== null &&
		distance > AT_UNIT_METRES &&
		posting.pollingUnitLatitude != null &&
		posting.pollingUnitLongitude != null
	) {
		return {
			kind: 'head-to-unit',
			place: posting.pollingUnitName || posting.entityCode,
			latitude: posting.pollingUnitLatitude,
			longitude: posting.pollingUnitLongitude,
		};
	}
	return { kind: 'checklist' };
};

/** Opens the phone's maps app with directions; Google Maps' URL scheme works on Android, iOS and desktop. */
export const directionsUrl = (latitude: number, longitude: number) =>
	`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
